import React from "react";
import RoomImageSlider from "./RoomImageSlider";
import RatePlanCard from "./RatePlanCard";
import { useTranslation } from "../../context/TranslationContext";

const RoomCard = ({ room, roomIndex = 0, selectedRooms, updateRoomQuantity, openRoomModal, guests, childAges, selectedDates, calculateNights, accommodationDetail, currentCurrency, onOpenLogin, isLoggedIn, memberDiscountPercent = 0 }) => {
  const { t } = useTranslation();
  // Truncate description to 120 characters
  const MAX_DESCRIPTION_LENGTH = 130;
  const description = room.description || '';
  const isDescriptionLong = description.length > MAX_DESCRIPTION_LENGTH;
  const truncatedDescription = isDescriptionLong 
    ? description.substring(0, MAX_DESCRIPTION_LENGTH).trim() + '...'
    : description;

  return (
    <div className="rounded-md border bg-white shadow">
      <div className="md:flex">
        <RoomImageSlider 
          images={room.images || [room.image]} 
          size="medium" 
          onImageClick={() => openRoomModal(room)}
          roomsAvailable={room.totalroom || null}
        />
    <div className="flex-1 flex flex-col justify-center p-6">
                              <div>
                              <h3 className="text-xl font-semibold mb-1" style={{color: 'var(--primary-color, #b45309)'}}>
                                {room.name}
                              </h3>
                              <p className="text-sm text-gray-600 mb-2">
                                {truncatedDescription}
                                {isDescriptionLong && (
                                  <button
                                    onClick={() => openRoomModal(room)}
                                    className="hover:opacity-80 font-medium ml-1"
                                    style={{color: 'var(--primary-color, #b45309)'}}
                                  >
                                    {t('room.readMore')}
                                  </button>
                                )}
                              </p>
                            </div>
                            <div className="flex items-center space-x-6 mt-2 mb-2 text-sm text-gray-600">
                              {room.guests && (
                              <div className="flex items-center space-x-1">
                                <svg
                                  className="h-4 w-4"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                                </svg>
                                <span>{t('room.upToGuests', { n: room.guests })}</span>
                              </div>
                              )}
                              {room.size && !isNaN(parseFloat(room.size)) && (
                              <div className="flex items-center space-x-1">
                                <svg
                                  className="h-4 w-4"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6z" />
                                </svg>
                                <span>{t('room.roomSize', { size: room.size })}</span>
                              </div>
                              )}
                              {room.BedSize && (
                                <div className="flex items-center space-x-1">
                                  <svg
                                    className="h-4 w-4"
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M7 14c1.66 0 3-1.34 3-3S8.66 8 7 8s-3 1.34-3 3 1.34 3 3 3zm0-4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm12-3h-8v8H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4zm2 8h-8V9h6c1.1 0 2 .9 2 2v4z"/>
                                  </svg>
                                  <span>{room.BedSize}</span>
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              {(room.amenities || [])
                                .slice(0, 4)
                                .map((amenity, index) => (
                                  <div
                                    key={index}
                                    className="flex items-center space-x-2 text-sm text-gray-600"
                                  >
                                    <svg
                                      className="h-4 w-4 text-green-500 flex-shrink-0"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <span>{amenity}</span>
                                  </div>
                                ))}
                            </div>
                            <div className="pt-2 border-t border-gray-200 flex items-center justify-between">
                              <button
                                onClick={() => openRoomModal(room)}
                                className="hover:opacity-80 text-sm font-medium underline underline-offset-2"
                                style={{color: 'var(--primary-color, #b45309)'}}
                              >
                                {t('room.viewDetails')}
                              </button>
                              {room.totalroom === 1 && (
                                <div className="flex items-center gap-1">
                                  <i
                                    className="fas fa-fire text-pink-600 text-xs"
                                    style={{ animation: 'fadeBlink 1.5s ease-in-out infinite' }}
                                  ></i>
                                  <span className="text-xs font-medium text-pink-600" style={{ animation: 'fadeBlink 1.5s ease-in-out infinite' }}>
                                    {t('room.onlyLeftAtPrice')}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          
      </div>

      <div className="mt-1 space-y-3 p-4">
        {(room.ratePlans || []).map((plan, planIndex) => {
          const selected = selectedRooms.find(it => it.room.id === room.id && it.ratePlan.id === plan.id);
          return (
            <RatePlanCard
              key={plan.id}
              room={room}
              plan={plan}
              isLowestRate={roomIndex === 0 && planIndex === 0}
              currentQuantity={selected?.quantity || 0}
              updateRoomQuantity={updateRoomQuantity}
              selectedRooms={selectedRooms}
              guests={guests}
              childAges={childAges}
              selectedDates={selectedDates}
              calculateNights={calculateNights}
              accommodationDetail={accommodationDetail}
              currentCurrency={currentCurrency}
              onOpenLogin={onOpenLogin}
              isLoggedIn={isLoggedIn}
              memberDiscountPercent={memberDiscountPercent}
            />
          );
        })}
      </div>
    </div>
  );
};

export default RoomCard;
